/**
 * GlobalSlackPanel — app-level host for SlackThreadPanel.
 *
 * Lets any part of the app open a Slack discussion thread on top of the current
 * view (without navigating) by dispatching:
 *
 *   window.dispatchEvent(new CustomEvent('open-slack-panel', { detail: {
 *     entityId, entityType, entityName, projectId, projectName, threadTs
 *   } }))
 *
 * Used by the notification bell so clicking a notification re-opens the chat in
 * context. The panel resolves the Slack channel server-side from projectId and
 * loads the thread from threadTs, so it works from anywhere.
 *
 * When a NEW thread is started through this global host (rather than an
 * entity's own inline SlackThreadPanel), the returned ts has nowhere else to
 * land — this dispatches 'slack-thread-started' ({ entityId, entityType, ts })
 * so whichever caller opened the panel can sync its own cache/state, the same
 * way SheetRow/MilestoneRow already do via their own onThreadStarted.
 */

import { useState, useEffect } from 'react'
import SlackThreadPanel from './SlackThreadPanel.jsx'

export default function GlobalSlackPanel() {
  const [cfg, setCfg] = useState(null)

  useEffect(() => {
    function onOpen(e) { setCfg(e.detail || null) }
    window.addEventListener('open-slack-panel', onOpen)
    return () => window.removeEventListener('open-slack-panel', onOpen)
  }, [])

  if (!cfg || !cfg.projectId) return null

  return (
    <SlackThreadPanel
      // Fresh instance per target so panel state resets when switching threads
      key={(cfg.entityId || '') + ':' + (cfg.threadTs || '')}
      entityId={cfg.entityId || ''}
      entityType={cfg.entityType || 'milestone'}
      entityName={cfg.entityName || ''}
      projectId={cfg.projectId}
      projectName={cfg.projectName || ''}
      threadTs={cfg.threadTs || ''}
      onClose={() => setCfg(null)}
      onThreadStarted={ts => {
        window.dispatchEvent(new CustomEvent('slack-thread-started', { detail: { entityId: cfg.entityId, entityType: cfg.entityType, ts } }))
      }}
    />
  )
}
